/* Receipt printing — 68mm thermal, silent iframe fire, KOTs + Bills. */
import { describeChoice, money } from "./pos";

export const RECEIPT_CSS = `
  @page{size:68mm auto;margin:0}
  html,body{margin:0;padding:0;background:#fff;color:#000}
  body{font-family:'Times New Roman','Liberation Serif','DejaVu Serif',Times,serif;font-size:12px;line-height:1.35;-webkit-print-color-adjust:exact;print-color-adjust:exact;word-wrap:break-word;overflow-wrap:break-word}
  .rc{width:62mm;max-width:66mm;margin:0 auto;padding:1.5mm 2mm;box-sizing:border-box}
  h1{font-size:15px;margin:0 0 1mm;text-align:center;letter-spacing:0.3px;font-weight:700;text-transform:uppercase}
  .sub{font-size:11px;text-align:center;font-style:italic;margin:0 0 1mm;line-height:1.25}
  .addr{font-size:10.5px;text-align:center;margin:0 0 0.6mm;line-height:1.3;word-wrap:break-word}
  h2{font-size:12px;margin:1.6mm 0 0.8mm;text-transform:uppercase;letter-spacing:0.3px;font-weight:700}
  hr{border:none;border-top:1px dashed #000;margin:1.2mm 0}
  .row{display:flex;justify-content:space-between;gap:3px;font-size:12px;word-wrap:break-word}
  .row span{flex:1 1 auto;min-width:0;overflow-wrap:break-word}
  .row b{font-weight:700;flex:0 0 auto;white-space:nowrap}
  .grand{font-weight:700;font-size:14px;border-top:2px solid #000;padding-top:1.2mm;margin-top:1.2mm}
  ul{padding:0;margin:1mm 0;list-style:none}
  li{margin:0.8mm 0;word-break:break-word;font-size:12px;line-height:1.35}
  li .qty{display:inline-block;min-width:5mm;font-weight:700}
  li em{font-style:italic}
  li .note{display:block;font-style:italic;padding-left:5mm;font-size:11px;color:#000;word-wrap:break-word}
  /* KOT (not bill): item name + variant + sub-variant + add-on names printed BOLD so
     the line-cook can read the ticket at a glance across a hot pass. */
  .rc:not(.bill) li{font-weight:700}
  .rc:not(.bill) li em{font-weight:700;font-style:italic}
  .rc:not(.bill) li .note{font-weight:600}
  .meta{text-align:center;font-size:11px;color:#000;font-weight:600;word-wrap:break-word}
  .cancel-banner{background:#000;color:#fff;text-align:center;padding:1.2mm 0;font-weight:700;letter-spacing:1.5px;margin:1.2mm 0;font-size:12px;font-family:'Times New Roman',serif}
  /* Bill variant — text roughly half-size vs. KOT so the whole invoice fits tightly on 68mm thermal. */
  .rc.bill{font-size:8.5px;line-height:1.3}
  .rc.bill h1{font-size:10.5px;margin:0 0 0.5mm}
  .rc.bill .sub{font-size:7.8px;margin:0 0 0.5mm}
  .rc.bill .addr{font-size:7.2px;margin:0 0 0.4mm}
  .rc.bill h2{font-size:8.5px;margin:1.2mm 0 0.6mm}
  .rc.bill .row{font-size:8.5px}
  .rc.bill .grand{font-size:10.5px;padding-top:0.9mm;margin-top:0.9mm}
  .rc.bill ul{margin:0.7mm 0}
  .rc.bill li{font-size:8.5px;line-height:1.3;margin:0.5mm 0}
  .rc.bill li .note{font-size:7.8px;padding-left:3mm}
  .rc.bill .meta{font-size:7.8px}
  .rc.bill .token-big{text-align:center;font-size:18px;font-weight:800;letter-spacing:1px;margin:1.5mm 0 0.8mm;font-family:'Times New Roman',serif}
  .rc.bill hr{border-top:1px dashed #000;margin:0.9mm 0}
  .rc.bill hr.station-sep{border-top:1.5px solid #000;margin:1.4mm 0}
  @media print{.rc{width:auto;max-width:66mm;padding:1mm 1.5mm}}
`;
export function openPrintWindow(title, htmlBody, variant = "") {
  const wrapperClass = variant ? `rc ${variant}` : "rc";
  // Use a hidden iframe rather than window.open — no popup blocker, no new tab,
  // and successive prints queue in the same session so the browser can reuse the
  // last-selected printer. If the operator has Chrome/Chromium launched with
  // `--kiosk-printing`, this fires WITHOUT any dialog.
  try {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "-10000px";
    iframe.style.bottom = "-10000px";
    iframe.style.width = "72mm";
    iframe.style.height = "1px";
    iframe.style.border = "0";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${RECEIPT_CSS}</style></head><body><div class="${wrapperClass}">${htmlBody}</div></body></html>`);
    doc.close();
    // One-shot fire — guarded so onload + safety-fallback + duplicate onload events
    // (browsers fire load multiple times after doc.write) never invoke print() twice.
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (_) { /* noop */ }
      // Keep the iframe long enough for the print dialog / driver to grab the DOM,
      // then remove so it doesn't leak memory.
      setTimeout(() => { try { iframe.remove(); } catch (_) { /* noop */ } }, 2500);
    };
    iframe.onload = fire;
    // Safety fallback in case onload never fires (some webkit variants after doc.write)
    setTimeout(fire, 500);
  } catch (e) {
    // As a last resort, fall back to popup — should never happen in Chromium
    const w = window.open("", "_blank", "width=340,height=640");
    if (!w) { alert("Unable to open print window — please check browser permissions."); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>${RECEIPT_CSS}</style></head><body><div class="${wrapperClass}">${htmlBody}</div></body></html>`);
    w.document.close();
    setTimeout(() => { try { w.print(); w.close(); } catch (_) { /* noop */ } }, 350);
  }
}
export const fmtNow = (iso) => new Date(iso || Date.now()).toLocaleString("en-IN");
export function escapeHtml(s) { return String(s ?? "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }
export function itemLineHtml(i, opts = {}) {
  const qty = opts.qty ?? (i.quantity - (i.cancelled_quantity || 0));
  if (qty <= 0 && !opts.showZero) return "";
  const choice = describeChoice(i.variant, i.addons);
  const bits = [`<span class="qty">${qty}×</span> ${escapeHtml(i.name)}`];
  if (choice) bits.push(` <em>(${escapeHtml(choice)})</em>`);
  const note = i.note ? `<div class="note">Note: ${escapeHtml(i.note)}</div>` : "";
  const price = opts.showPrice ? `<span style="float:right">${money(qty * i.price)}</span>` : "";
  return `<li>${price}${bits.join("")}${note}</li>`;
}
export function stationKotsForOrder(order, stations, opts = {}) {
  // Returns array of {station, items[]}.
  // opts.excludeExpo — when true (used for physical printing), stations with no
  // `assigned_categories` (Expo / Pass — sees everything) are skipped so we don't
  // print a duplicate consolidated KOT alongside every production station's ticket.
  const { excludeExpo = false } = opts;
  const groups = [];
  for (const s of stations) {
    const cats = new Set(s.assigned_categories || []);
    if (excludeExpo && cats.size === 0) continue;
    let items;
    if (cats.size === 0) items = order.items;
    else items = order.items.filter((i) => cats.has(i.category));
    if (!items.length) continue;
    groups.push({ station: s, items });
  }
  return groups;
}
export function printOrderKOTs(order, stations, { cancelled = false, cancelledItems = null, addedItems = null, stationIds = null, duplicate = false } = {}) {
  const groups = stationKotsForOrder(order, stations, { excludeExpo: true });
  const filtered = stationIds ? groups.filter((g) => stationIds.includes(g.station.id)) : groups;
  filtered.forEach(({ station, items }) => {
    const cats = station.assigned_categories || [];
    let renderItems = items;
    let banner = "";
    if (cancelledItems) {
      renderItems = cancelledItems.filter((ci) => (cats.length === 0) || cats.includes(ci.category));
      if (!renderItems.length) return;
    } else if (addedItems) {
      // supplementary KOT — only newly-added items belonging to this station
      renderItems = addedItems.filter((ai) => (cats.length === 0) || cats.includes(ai.category));
      if (!renderItems.length) return;
      banner = '<div class="cancel-banner" style="background:#2b7d3b">ADDITIONAL ITEMS</div>';
    } else if (duplicate) {
      banner = '<div class="cancel-banner" style="background:#555">DUPLICATE KOT</div>';
    }
    const rows = renderItems.map((i) => (cancelled || cancelledItems)
      ? itemLineHtml(i, { qty: i.quantity, showZero: true })
      : itemLineHtml(i, { qty: i.quantity - (i.cancelled_quantity || 0) })
    ).filter(Boolean).join("");
    if (!rows) return;
    const cashierName = order.created_by_name || order.billed_by_name || "";
    const byRow = cashierName ? `<div class="row"><span>By</span><b>${escapeHtml(cashierName)}</b></div>` : "";
    const html = `
      <h1>KITCHEN ORDER TICKET</h1>
      ${cancelled ? '<div class="cancel-banner">CANCELLED</div>' : banner}
      <div class="meta">${station.gst_number ? `${escapeHtml(station.gst_number)} · ` : ""}${escapeHtml(station.name)}</div>
      <hr>
      <div class="row"><span>Order #</span><b>${order.order_number != null ? String(order.order_number).padStart(4, "0") : escapeHtml(order.id.slice(-8))}</b></div>
      <div class="row"><span>Token</span><b>T${String(order.token_number || "").padStart(2, "0")}</b></div>
      <div class="row"><span>Table</span><b>${escapeHtml(order.table || (order.order_type === "table" ? "Dine-in" : "Takeaway"))}</b></div>
      <div class="row"><span>Date</span><b>${fmtNow(order.created_at)}</b></div>
      ${byRow}
      <hr>
      <ul>${rows}</ul>
      <hr>
      <div class="meta">${cancelled ? "REMOVE FROM PASS" : addedItems ? "PLEASE ADD TO EXISTING TICKET" : "Prepare & mark ready"}</div>
    `;
    openPrintWindow(`KOT · ${station.name} · #${order.order_number || order.token_number}`, html);
  });
}
export function printBill(order, stations, opts = {}) {
  const { settings = null, isDuplicate = false } = opts;
  const cfg = {
    title: "Shimla QoH Food Court",
    subtitle: "(A Unit of KKK Food Court Pvt Ltd)",
    address: "Near Lift to the Mall, Cart Road, Shimla, H.P., 171001",
    phone: "9773970042",
    website: "shimlafoodcourt.com",
    email: "contact@shimlafoodcourt.com",
    fssai: "10925013000153",
    footer_note: "Thanks for dining with us!",
    ...(settings || {}),
  };
  const groups = stationKotsForOrder(order, stations, { excludeExpo: true });
  // ---- Per-station tax computation ----
  const taxMode = order.tax_mode === "inter" ? "inter" : "intra";
  const taxRate = Number(order.tax_rate != null ? order.tax_rate : 0.05);
  const orderSubtotal = groups.reduce((sum, { items }) => sum + items.reduce(
    (a, i) => a + Number(i.price || 0) * Math.max(0, (i.quantity || 0) - (i.cancelled_quantity || 0)), 0), 0);
  const discountTotal = Math.min(Math.max(Number(order.discount || 0), 0), orderSubtotal);
  const stationRows = groups.map(({ station, items }) => {
    const s_sub = items.reduce((a, i) => a + Number(i.price || 0) * Math.max(0, (i.quantity || 0) - (i.cancelled_quantity || 0)), 0);
    // Pro-rate the discount over station subtotals so per-station GST reflects the effective taxable amount.
    const s_disc = orderSubtotal > 0 ? (s_sub / orderSubtotal) * discountTotal : 0;
    const s_taxable = Math.max(0, s_sub - s_disc);
    let s_cgst = 0, s_sgst = 0, s_igst = 0;
    if (taxMode === "inter") {
      s_igst = s_taxable * taxRate;
    } else {
      s_cgst = s_taxable * (taxRate / 2);
      s_sgst = s_taxable * (taxRate / 2);
    }
    return { station, items, s_sub, s_disc, s_taxable, s_cgst, s_sgst, s_igst };
  });
  const grandItems = stationRows.reduce((a, r) => a + r.s_sub, 0);
  const grandDiscount = stationRows.reduce((a, r) => a + r.s_disc, 0);
  const grandTaxable = stationRows.reduce((a, r) => a + r.s_taxable, 0);
  const grandGST = stationRows.reduce((a, r) => a + r.s_cgst + r.s_sgst + r.s_igst, 0);
  const grandCGST = stationRows.reduce((a, r) => a + r.s_cgst, 0);
  const grandSGST = stationRows.reduce((a, r) => a + r.s_sgst, 0);
  const grandIGST = stationRows.reduce((a, r) => a + r.s_igst, 0);
  const rawBillTotal = grandTaxable + grandGST;
  const billTotal = Math.floor(rawBillTotal + 0.5);
  const rounding = billTotal - rawBillTotal;

  // Station blocks with items + station-level tax. Each station block ends with a bold
  // separator so multi-kitchen bills are visually chunked when reviewed on the counter.
  const stationBlocks = stationRows.map(({ station, items, s_sub, s_disc, s_taxable, s_cgst, s_sgst, s_igst }) => {
    const lines = items.map((i) => itemLineHtml(i, { showPrice: true })).filter(Boolean).join("");
    if (!lines) return "";
    const gstPrefix = station.gst_number ? `<small class="gst-prefix">${escapeHtml(station.gst_number)} · </small>` : "";
    const taxRows = (taxMode === "inter")
      ? `<div class="row"><span>IGST (${(taxRate * 100).toFixed(2)}%)</span><b>${money(s_igst)}</b></div>`
      : `<div class="row"><span>CGST (${(taxRate * 50).toFixed(2)}%)</span><b>${money(s_cgst)}</b></div>
         <div class="row"><span>SGST (${(taxRate * 50).toFixed(2)}%)</span><b>${money(s_sgst)}</b></div>`;
    const dp = Number(order.discount_percent || 0);
    const discLabel = dp > 0 ? `Discount ${dp}%` : "Discount";
    const discRow = s_disc >= 0.005
      ? `<div class="row"><span>${discLabel}</span><b>-${money(s_disc)}</b></div>` : "";
    return `<h2>${gstPrefix}${escapeHtml(station.name)}</h2>
      <ul>${lines}</ul>
      <div class="row"><span>Station subtotal</span><b>${money(s_sub)}</b></div>
      ${discRow}
      ${taxRows}
      <div class="row"><b>Station total</b><b>${money(s_taxable + s_cgst + s_sgst + s_igst)}</b></div>
      <hr class="station-sep">`;
  }).join("");

  const rndBlock = Math.abs(rounding) >= 0.005
    ? `<div class="row"><span>Round off</span><b>${rounding >= 0 ? "+" : ""}${money(rounding)}</b></div>` : "";
  const gstSummary = (taxMode === "inter")
    ? `<div class="row"><span>Grand IGST (${(taxRate * 100).toFixed(2)}%)</span><b>${money(grandIGST)}</b></div>`
    : `<div class="row"><span>Grand CGST</span><b>${money(grandCGST)}</b></div>
       <div class="row"><span>Grand SGST</span><b>${money(grandSGST)}</b></div>`;

  const headerLines = [
    `<h1>${escapeHtml(cfg.title)}</h1>`,
    cfg.subtitle ? `<div class="sub">${escapeHtml(cfg.subtitle)}</div>` : "",
    cfg.address ? `<div class="addr">${escapeHtml(cfg.address)}</div>` : "",
    (cfg.phone || cfg.website) ? `<div class="addr">${cfg.phone ? "Ph: " + escapeHtml(cfg.phone) : ""}${cfg.phone && cfg.website ? " · " : ""}${cfg.website ? escapeHtml(cfg.website) : ""}</div>` : "",
    cfg.email ? `<div class="addr">${escapeHtml(cfg.email)}</div>` : "",
    cfg.fssai ? `<div class="addr">FSSAI: ${escapeHtml(cfg.fssai)}</div>` : "",
  ].filter(Boolean).join("");

  const html = `
    ${headerLines}
    <div class="meta">${isDuplicate ? "TAX INVOICE — DUPLICATE" : "TAX INVOICE"}</div>
    <div class="token-big">Token no: T${String(order.token_number || "").padStart(2, "0")}</div>
    <hr>
    <div class="row"><span>Bill #</span><b>${order.order_number != null ? String(order.order_number).padStart(4, "0") : escapeHtml(order.id.slice(-8))}</b></div>
    <div class="row"><span>Table</span><b>${escapeHtml(order.table || (order.order_type === "table" ? "Dine-in" : "Takeaway"))}</b></div>
    <div class="row"><span>Date</span><b>${fmtNow(order.created_at)}</b></div>
    ${order.payment_method ? `<div class="row"><span>Payment</span><b>${escapeHtml(order.payment_method.toUpperCase())}</b></div>` : ""}
    ${(order.billed_by_name || order.created_by_name) ? `<div class="row"><span>Cashier</span><b>${escapeHtml(order.billed_by_name || order.created_by_name)}</b></div>` : ""}
    <hr class="station-sep">
    ${stationBlocks}
    <div class="row"><span>Grand total (items)</span><b>${money(grandItems)}</b></div>
    ${grandDiscount >= 0.005 ? `<div class="row"><span>Total discount${order.discount_reason ? ` · ${escapeHtml(order.discount_reason)}` : ""}</span><b>-${money(grandDiscount)}</b></div>` : ""}
    ${gstSummary}
    <div class="row"><span>Grand total (GST)</span><b>${money(grandGST)}</b></div>
    ${rndBlock}
    <div class="row grand"><span>TOTAL BILL</span><b>${money(billTotal)}</b></div>
    <hr>
    <div class="meta">${escapeHtml(cfg.footer_note || "Thanks for dining with us!")}</div>
  `;
  openPrintWindow(`Bill · ${order.id.slice(-8)}`, html, "bill");
}
